import React, { createContext, useContext, useRef } from 'react';

interface WorkspaceCache {
  [workspaceId: string]: {
    owner_org_id: string;
    timestamp: number;
  };
}

interface OrganizationsCache {
  data: any;
  timestamp: number;
  promise?: Promise<any>;
}

interface CustomFieldsCacheContextType {
  getWorkspace: (workspaceId: string) => any | null;
  setWorkspace: (workspaceId: string, data: any) => void;
  /** In-flight GET /workspaces/:id shared by every badge mounted in the same tick (#1179). */
  getWorkspacePromise: (workspaceId: string) => Promise<any> | null;
  setWorkspacePromise: (workspaceId: string, promise: Promise<any>) => void;
  clearWorkspacePromise: (workspaceId: string) => void;
  getOrganizations: () => any | null;
  setOrganizations: (data: any) => void;
  getOrganizationsPromise: () => Promise<any> | null;
  setOrganizationsPromise: (promise: Promise<any>) => void;
  clearOrganizationsPromise: () => void;
  getCount: (entityType: string, entityId: string, workspaceId: string) => number | null;
  setCount: (entityType: string, entityId: string, workspaceId: string, count: number) => void;
  getBatchCountPromise: (key: string) => Promise<any> | null;
  setBatchCountPromise: (key: string, promise: Promise<any>) => void;
  clearBatchCountPromise: (key: string) => void;
}

const CustomFieldsCacheContext = createContext<CustomFieldsCacheContextType | null>(null);

const CACHE_TTL = 60000; // 1 minute

interface CountCache {
  [key: string]: { // key format: "entityType:entityId:workspaceId"
    count: number;
    timestamp: number;
  };
}

interface BatchCountPromises {
  [key: string]: Promise<any>; // key format: "entityType:workspaceId"
}

export const CustomFieldsCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const workspaceCache = useRef<WorkspaceCache>({});
  const organizationsCache = useRef<OrganizationsCache | null>(null);
  const countCache = useRef<CountCache>({});
  const batchCountPromises = useRef<BatchCountPromises>({});
  const workspacePromises = useRef<Record<string, Promise<any>>>({});

  const getWorkspace = (workspaceId: string) => {
    const cached = workspaceCache.current[workspaceId];
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > CACHE_TTL) {
      delete workspaceCache.current[workspaceId];
      return null;
    }
    
    return cached;
  };

  const setWorkspace = (workspaceId: string, data: any) => {
    workspaceCache.current[workspaceId] = {
      ...data,
      timestamp: Date.now()
    };
  };

  const getWorkspacePromise = (workspaceId: string) => workspacePromises.current[workspaceId] || null;
  const setWorkspacePromise = (workspaceId: string, promise: Promise<any>) => {
    workspacePromises.current[workspaceId] = promise;
  };
  const clearWorkspacePromise = (workspaceId: string) => {
    delete workspacePromises.current[workspaceId];
  };

  const getOrganizations = () => {
    if (!organizationsCache.current) return null;
    
    if (Date.now() - organizationsCache.current.timestamp > CACHE_TTL) {
      organizationsCache.current = null;
      return null;
    }
    
    return organizationsCache.current.data;
  };

  const setOrganizations = (data: any) => {
    organizationsCache.current = {
      data,
      timestamp: Date.now()
    };
  };

  const getOrganizationsPromise = () => {
    return organizationsCache.current?.promise || null;
  };

  const setOrganizationsPromise = (promise: Promise<any>) => {
    if (!organizationsCache.current) {
      organizationsCache.current = {
        data: null,
        timestamp: Date.now(),
        promise
      };
    } else {
      organizationsCache.current.promise = promise;
    }
  };

  const clearOrganizationsPromise = () => {
    if (organizationsCache.current) {
      delete organizationsCache.current.promise;
    }
  };

  const getCount = (entityType: string, entityId: string, workspaceId: string) => {
    const key = `${entityType}:${entityId}:${workspaceId}`;
    const cached = countCache.current[key];
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > CACHE_TTL) {
      delete countCache.current[key];
      return null;
    }
    
    return cached.count;
  };

  const setCount = (entityType: string, entityId: string, workspaceId: string, count: number) => {
    const key = `${entityType}:${entityId}:${workspaceId}`;
    countCache.current[key] = {
      count,
      timestamp: Date.now()
    };
  };

  const getBatchCountPromise = (key: string) => {
    return batchCountPromises.current[key] || null;
  };

  const setBatchCountPromise = (key: string, promise: Promise<any>) => {
    batchCountPromises.current[key] = promise;
  };

  const clearBatchCountPromise = (key: string) => {
    delete batchCountPromises.current[key];
  };

  return (
    <CustomFieldsCacheContext.Provider
      value={{
        getWorkspace,
        setWorkspace,
        getWorkspacePromise,
        setWorkspacePromise,
        clearWorkspacePromise,
        getOrganizations,
        setOrganizations,
        getOrganizationsPromise,
        setOrganizationsPromise,
        clearOrganizationsPromise,
        getCount,
        setCount,
        getBatchCountPromise,
        setBatchCountPromise,
        clearBatchCountPromise
      }}
    >
      {children}
    </CustomFieldsCacheContext.Provider>
  );
};

export const useCustomFieldsCache = () => {
  const context = useContext(CustomFieldsCacheContext);
  if (!context) {
    throw new Error('useCustomFieldsCache must be used within CustomFieldsCacheProvider');
  }
  return context;
};

